import React, { memo, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  useAnimatedReaction,
  runOnJS,
  interpolate,
  interpolateColor,
  Extrapolation,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { MenuFlowDateSlider } from './MenuFlowDateSlider';
import { MenuFlowFoodTypeBar } from './MenuFlowFoodTypeBar';
import { MenuFlowSectionHeader } from './MenuFlowSectionHeader';
import { MenuFlowCountersRow } from './MenuFlowCountersRow';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { androidStripElevationSeams, androidHeaderTopInset, MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT } from '../../shared/theme/sharedStyles';
import { MenuFlowSection } from '../../data/domains/foodMenu';
import { MENU_FLOW_COUNTERS_BLOCK_HEIGHT } from './MenuFlowCountersRow';
import {
  MENU_FLOW_STUDIO_BLOCK_GAP,
  MENU_FLOW_STUDIO_FOOD_TYPE_HEIGHT,
  MENU_FLOW_STUDIO_SECTION_BAND_HEIGHT,
} from './menuFlowHeaderLayout';

const block_first = MENU_FLOW_HEADER_DATE_BLOCK_HEIGHT;
const block_second = MENU_FLOW_COUNTERS_BLOCK_HEIGHT;
const block_third = MENU_FLOW_STUDIO_FOOD_TYPE_HEIGHT;
const block_fourth = MENU_FLOW_STUDIO_SECTION_BAND_HEIGHT;
const block_gap = MENU_FLOW_STUDIO_BLOCK_GAP;

function createMenuFlowHeaderStyles(c: ThemeColors) {
  return StyleSheet.create({
    headerShell: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      // overflow: 'hidden',
      flexDirection: 'column',
      backgroundColor: c.surface,
      // borderBottomWidth: StyleSheet.hairlineWidth,
      // borderBottomColor: c.borderSubtle,
      shadowColor: c.shadowColor,
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.01,
      shadowRadius: 5,
      elevation: 12,
      // backfaceVisibility: 'hidden',
      ...androidStripElevationSeams(),
    },
    content: {
      width: '100%',
      paddingHorizontal: 12,
    },
    syncingRow: {
      position: 'absolute',
      right: 20,
      top: 60,
      zIndex: 1001,
    },
    staggerBlock: {
      borderRadius: appTheme.borderRadius.pill,
      // borderBottomWidth: StyleSheet.hairlineWidth,
      // borderBottomColor: c.border,
      // shadowColor: c.shadowColor,
      // shadowOffset: { width: 0, height: 1 },
      // shadowOpacity: 0.1,
      // shadowRadius: 1,
      // elevation: 12,
      // backfaceVisibility: 'hidden',
      // overflow: 'hidden',
      // ...androidStripElevationSeams(), 
    },
    staggerBlockFirst: {
      backgroundColor: c.primary,
      paddingHorizontal: 8,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      marginBottom: block_gap,
    },
    staggerBlockSecond: {
      marginBottom: block_gap,
      height: block_second,
      padding: 4,
      borderRadius: appTheme.borderRadius.pill,
    },
    staggerBlockThird: {
      height: block_third,
      paddingVertical: 4,
      paddingHorizontal: 4,
      borderRadius: appTheme.borderRadius.pill,
      justifyContent: 'center',
    },
    staggerBlockFourth: {
      height: block_fourth,
      padding: 4,
      borderRadius: appTheme.borderRadius.pill,
    },
  });
}

export interface MenuFlowHeaderProps {
  scrollY: SharedValue<number>;
  scrollRange: number;
  fullHeaderHeight: number;
  collapsedHeaderHeight: number;
  dateSliderDates: string[];
  selectableDates: string[];
  selectedDate: string;
  onDateSelect: (date: string) => void;
  dayConfigMap: { [date: string]: any };
  foodTypes: string[];
  foodTypeLabels?: Record<string, string>;
  selectedFoodType: string;
  onSelectFoodType: (type: string) => void;
  mealsCount: number;
  snacksCount: number;
  selectedMealsCount: number;
  selectedSnacksCount: number;
  isSyncing?: boolean;
  lastDateSliderCollapsedSV: SharedValue<boolean>;
  reduceMotion?: boolean;
  sections: MenuFlowSection[];
  currentSectionIndexSV: SharedValue<number>;
  isDoubleBoxForDate?: boolean;
  activeBox?: 'box1' | 'box2';
  onBoxSelect?: (box: 'box1' | 'box2') => void;
  forcedDisabledDates?: string[];
  onCancelBox?: () => void;
  onRequestDoubleBox?: () => void;
}

function MenuFlowHeaderComponent({
  scrollY,
  scrollRange,
  fullHeaderHeight,
  collapsedHeaderHeight,
  dateSliderDates,
  selectableDates,
  selectedDate,
  onDateSelect,
  dayConfigMap,
  foodTypes,
  foodTypeLabels,
  selectedFoodType,
  onSelectFoodType,
  mealsCount,
  snacksCount,
  selectedMealsCount,
  selectedSnacksCount,
  isSyncing = false,
  lastDateSliderCollapsedSV,
  reduceMotion = false,
  sections = [],
  currentSectionIndexSV,
  isDoubleBoxForDate = false,
  activeBox = 'box1',
  onBoxSelect,
  forcedDisabledDates = [],
  onCancelBox,
  onRequestDoubleBox,
}: MenuFlowHeaderProps) {
  const insets = useSafeAreaInsets();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createMenuFlowHeaderStyles(themeColors), [themeColors]);
  const [localSectionIndex, setLocalSectionIndex] = useState(0);
  const [isDateSliderCollapsedLocal, setIsDateSliderCollapsedLocal] = useState(false);

  useAnimatedReaction(
    () => currentSectionIndexSV.value,
    idx => {
      runOnJS(setLocalSectionIndex)(Math.max(0, Math.floor(idx)));
    },
    [currentSectionIndexSV],
  );

  useAnimatedReaction(
    () => lastDateSliderCollapsedSV.value,
    collapsed => {
      runOnJS(setIsDateSliderCollapsedLocal)(collapsed);
    },
    [lastDateSliderCollapsedSV],
  );

  const safe_top_inset = androidHeaderTopInset(insets.top);
  const collapsed_shell_height = collapsedHeaderHeight + safe_top_inset;

  const current_section = useMemo(
    () => sections[localSectionIndex] ?? sections[0] ?? null,
    [sections, localSectionIndex],
  );

  const is_limit_reached = Boolean(
    current_section &&
      current_section.displayLimit > 0 &&
      current_section.selectedQuantity >= current_section.displayLimit,
  );
  const progress_limit_reached = useSharedValue(is_limit_reached ? 1 : 0);

  useEffect(() => {
    progress_limit_reached.value = reduceMotion
      ? withTiming(is_limit_reached ? 1 : 0, { duration: 160 })
      : withSpring(is_limit_reached ? 1 : 0, { stiffness: 380, damping: 42 });
  }, [is_limit_reached, progress_limit_reached, reduceMotion]);

  const progress = useDerivedValue(() => interpolate(scrollY.value, [0, scrollRange], [0, 1], Extrapolation.CLAMP));

  // Expanded: DATE_BLOCK + flex-end clears the status bar (DateSlider must not also pad inset).
  // Collapsed: shell paddingTop carries the inset once the date band is gone.
  const headerShellStyle = useAnimatedStyle(() => ({
    height: interpolate(progress.value, [0, 0.9], [fullHeaderHeight, collapsed_shell_height], Extrapolation.CLAMP),
    paddingTop: interpolate(progress.value, [0, 1], [0, Math.max(0, safe_top_inset - 10)], Extrapolation.CLAMP),
  }), [collapsed_shell_height, fullHeaderHeight, safe_top_inset]);

  const dateSliderWrapStyle = useAnimatedStyle(() => ({
    height: interpolate(progress.value, [0, 0.8], [block_first, 0], Extrapolation.CLAMP),
    transform: [{ translateY: interpolate(progress.value, [0, 0.8], [0, -60], Extrapolation.CLAMP) }],
    marginBottom: interpolate(progress.value, [0, 1], [block_gap, 12], Extrapolation.CLAMP),
  }));

  const countersWrapStyle = useAnimatedStyle(() => {
    const scroll_bg = interpolateColor(progress.value, [0, 1], [themeColors.borderLight, themeColors.backgroundElevated], 'RGB');
    return {
      marginBottom: interpolate(progress.value, [0, 1], [block_gap, 4], Extrapolation.CLAMP),
      backgroundColor: activeBox === 'box2' ? themeColors.backgroundElevated : scroll_bg,
    };
  }, [themeColors.borderLight, themeColors.backgroundElevated, activeBox]);

  const foodTypeWrapStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(progress.value, [0, 1], [themeColors.borderLight, themeColors.backgroundElevated], 'RGB'),
  }), [themeColors.borderLight, themeColors.backgroundElevated]);

  const sectionHeaderWrapStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(progress_limit_reached.value, [0, 1], [themeColors.backgroundElevated, themeColors.primary], 'RGB'),
    marginTop: interpolate(progress.value, [0, 1], [block_gap, 4], Extrapolation.CLAMP),
  }), [themeColors.borderLight, themeColors.primary]);

  return (
    <Animated.View style={[styles.headerShell, headerShellStyle]} pointerEvents="box-none">
      <View style={styles.content}>
        {isSyncing ? (
          <View style={styles.syncingRow}>
            <ButtonLoader size={18} color={themeColors.primary} />
          </View>
        ) : null}

        <Animated.View
          style={[styles.staggerBlock, styles.staggerBlockFirst, dateSliderWrapStyle]}
          pointerEvents={isDateSliderCollapsedLocal ? 'none' : 'auto'}>
          <MenuFlowDateSlider
            availableDates={dateSliderDates}
            selectableDates={selectableDates}
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            dayConfigMap={dayConfigMap}
            forcedDisabledDates={forcedDisabledDates}
          />
        </Animated.View>

        <Animated.View style={[styles.staggerBlock, styles.staggerBlockSecond, countersWrapStyle]}>
          <MenuFlowCountersRow
            mealsCount={mealsCount}
            snacksCount={snacksCount}
            selectedMealsCount={selectedMealsCount}
            selectedSnacksCount={selectedSnacksCount}
            isDoubleBoxForDate={isDoubleBoxForDate}
            activeBox={activeBox}
            onBoxSelect={onBoxSelect}
            onCancelBox={onCancelBox}
            onRequestDoubleBox={onRequestDoubleBox}
          />
        </Animated.View>

        <Animated.View style={[styles.staggerBlock, styles.staggerBlockThird, foodTypeWrapStyle]}>
          <MenuFlowFoodTypeBar
            foodTypes={foodTypes}
            foodTypeLabels={foodTypeLabels}
            selectedFoodType={selectedFoodType}
            onSelectFoodType={onSelectFoodType}
          />
        </Animated.View>

        <Animated.View style={[styles.staggerBlock, styles.staggerBlockFourth, sectionHeaderWrapStyle]}>
          <MenuFlowSectionHeader currentSection={current_section} />
        </Animated.View>
      </View>
    </Animated.View>
  );
}

export const MenuFlowHeader = memo(MenuFlowHeaderComponent);
export default MenuFlowHeader;
